import * as React from "react";
import { cn } from "@/lib/utils";
import { 
  Text, 
  Heading 
} from '../design-system';

/**
 * LegalContent Adapter Component
 * 
 * A specialized content adapter for legal pages that provides consistent styling
 * and semantic markup for legal documents following the design system guidelines.
 */
interface LegalContentProps {
  children: React.ReactNode;
  className?: string;
}

export function LegalContent({ children, className }: LegalContentProps) {
  return (
    <div className={cn("legal-content prose prose-lg dark:prose-invert max-w-none", className)}>
      {children}
    </div>
  );
}

/**
 * LegalSection component for organizing legal content into sections
 */
interface LegalSectionProps {
  title: string;
  id?: string;
  children: React.ReactNode;
  className?: string;
}

export function LegalSection({ title, id, children, className }: LegalSectionProps) {
  return (
    <section id={id} className={cn("mb-10", className)}>
      <Heading 
        level={2}
        color="primary"
        className="mb-4 text-primary-700 dark:text-primary-300"
      >
        {title}
      </Heading>
      {children}
    </section>
  );
}

/**
 * LegalSubsection component for organizing content within legal sections
 */
interface LegalSubsectionProps {
  title: string;
  id?: string;
  children: React.ReactNode;
  className?: string;
}

export function LegalSubsection({ title, id, children, className }: LegalSubsectionProps) {
  return (
    <div id={id} className={cn("mb-6", className)}>
      <Heading 
        level={3}
        style="default"
        className="mb-3 text-gray-900 dark:text-gray-100"
      >
        {title}
      </Heading>
      {children}
    </div>
  );
}

/**
 * LegalParagraph for consistent paragraph styling in legal documents
 */
interface LegalParagraphProps {
  children: React.ReactNode;
  className?: string;
}

export function LegalParagraph({ children, className }: LegalParagraphProps) {
  return (
    <Text 
      variant="body"
      color="secondary"
      className={cn("mb-4 text-gray-700 dark:text-gray-300 leading-relaxed", className)}
    >
      {children}
    </Text>
  );
}

/**
 * LegalList for consistent list styling in legal documents
 */
interface LegalListProps {
  items: React.ReactNode[];
  ordered?: boolean;
  className?: string;
}

export function LegalList({ items, ordered = false, className }: LegalListProps) {
  const ListComponent = ordered ? 'ol' : 'ul';
  
  return (
    <ListComponent 
      className={cn(
        "mb-6 pl-6",
        ordered ? "list-decimal" : "list-disc",
        className
      )}
    >
      {items.map((item, index) => (
        <li key={index} className="mb-2 text-gray-700 dark:text-gray-300">
          {item}
        </li>
      ))}
    </ListComponent>
  );
}

/**
 * LegalDefinition for terms and definitions in legal documents
 */
interface LegalDefinitionProps {
  term: string;
  children: React.ReactNode;
  className?: string;
}

export function LegalDefinition({ term, children, className }: LegalDefinitionProps) {
  return (
    <div className={cn("mb-4", className)}>
      <Text 
        variant="body"
        weight="semibold"
        className="font-semibold text-gray-900 dark:text-gray-100"
      >
        {term}
      </Text>
      <Text 
        variant="body"
        color="secondary"
        className="ml-6 text-gray-700 dark:text-gray-300"
      >
        {children}
      </Text>
    </div>
  );
}

/**
 * LegalHighlight for important notes in legal documents
 */
interface LegalHighlightProps {
  children: React.ReactNode;
  className?: string;
}

export function LegalHighlight({ children, className }: LegalHighlightProps) {
  return (
    <div className={cn(
      "mb-6 p-4 border-l-4 border-primary-500 bg-primary-50 dark:bg-gray-800 dark:border-primary-400 premium-radius-md",
      className
    )}>
      <Text 
        variant="body"
        className="text-gray-800 dark:text-gray-200"
      >
        {children}
      </Text>
    </div>
  );
} 